import { z } from 'zod';

import { CLAIM_CAUSE_VALUES, CLAIM_RESOLVER_VALUES, CLAIM_STATUS_VALUES, LINE_STATE_VALUES } from '../fieldEnums';
import { doc, documentedStringEnum, itemCountField, moneyField, optionalDocumentedStringEnum } from './openapiHelpers';

const fileSchema = z.object({
    id: doc(z.number().int().optional(), 'File ID when updating an existing attachment.'),
    data: doc(z.string().optional(), 'Base64 content. Required for new files.'),
    fileName: doc(z.string(), 'Original file name.'),
    type: doc(z.string().min(1), 'Attachment category (free-form string, same as v2).'),
    fileType: doc(z.string(), 'MIME type or extension.'),
    fileSize: doc(z.string().optional(), 'File size.'),
    createdAt: doc(z.union([z.string(), z.date()]).nullish(), 'Upload timestamp from files.CREATED_AT.'),
    creatorRole: doc(
        z.enum(['skladon_employee', 'client']).nullish(),
        'Who uploaded the file (Skladon employee or client).',
    ),
}).strict();

const claimProductSchema = z.object({
    sku: doc(z.string().min(1), 'Claimed product SKU.'),
    claimedQuantity: itemCountField(),
    resendQuantity: doc(z.number().int().nullish(), 'Quantity of the product to be resent as part of the claim resolution.'),
    lineState: documentedStringEnum(
        LINE_STATE_VALUES,
        'Line state. E = enabled, D = disabled. If omitted on create, defaults to E.',
    ).optional(),
}).strict();

const claimV21WritableFields = {
    description: doc(z.string(), 'What the claim is about. Required when creating or updating a claim.'),
    eshopNote: doc(z.string().nullish(), 'E-shop note; writable on POST/PUT/PATCH and always returned in v2.1.'),
    claimCause: documentedStringEnum(CLAIM_CAUSE_VALUES, 'Cause of the claim. Required on create/update.'),
    causeOtherType: doc(z.string().nullish(), 'Required when claimCause is others.'),
    files: doc(z.array(fileSchema).optional(), 'Photos and documents attached to the claim.'),
    products: doc(z.array(claimProductSchema).optional(), 'Claimed order lines.'),
} as const;

export const createClaimV21Schema = z.object({
    eshopClaimId: doc(z.string().min(1), 'Your claim identifier — unique within the eshop.'),
    eshopOrderId: doc(z.string().min(1), 'Related order identifier.'),
    ...claimV21WritableFields,
}).strict();

export const updateClaimV21Schema = z.object({
    ...claimV21WritableFields,
}).strict();

export const patchClaimV21Schema = z.object({
    ...claimV21WritableFields,
}).partial().strict().refine(
    (data) => Object.keys(data).length > 0,
    { message: 'At least one field must be provided' },
);

const claimV21ResponseReadOnly = {
    id: doc(z.number().int().optional(), 'Internal Skladon claim ID.'),
    fcn: doc(z.string().nullish(), 'Skladon FCN (claim) number.'),
    orderId: doc(z.number().int().nullish(), 'Internal Skladon order ID.'),
    orderType: doc(z.string().nullish(), 'Order type of the related order.'),
    shipper: doc(z.string().nullish(), 'Shipper of the related order.'),
    trackingNumber: doc(z.string().nullish(), 'Outbound tracking number.'),
    returnTrackingNumber: doc(z.string().nullish(), 'Return tracking number, when applicable.'),
    cctv: doc(z.boolean().nullish(), 'Whether CCTV evidence is available.'),
    status: optionalDocumentedStringEnum(CLAIM_STATUS_VALUES, 'Claim workflow status (read-only).'),
    createdAt: doc(z.string().nullish(), 'Claim creation timestamp.'),
    updatedAt: doc(z.string().nullish(), 'Last update timestamp.'),
    closedAt: doc(z.string().nullish(), 'When the claim was closed.'),
    inProcessAt: doc(z.string().nullish(), 'When the claim entered inProcess.'),
    moreInfoNeededAt: doc(z.string().nullish(), 'When more info was requested.'),
    informationAddedAt: doc(z.string().nullish(), 'When additional information was added.'),
    rejectedAt: doc(z.string().nullish(), 'When the claim was rejected.'),
    canceledAt: doc(z.string().nullish(), 'When the claim was canceled.'),
    acceptedExpectCompensationAt: doc(z.string().nullish(), 'When the claim was accepted pending compensation.'),
    acceptedPendingClientCompensationAt: doc(
        z.string().nullish(),
        'When the claim was accepted pending client compensation.',
    ),
    resolutionText: doc(z.string().nullish(), 'Resolution summary from Skladon.'),
    resolver: optionalDocumentedStringEnum(CLAIM_RESOLVER_VALUES, 'Who resolved the claim (read-only).'),
    transportPrice: moneyField(),
    goodsPrice: moneyField(),
    totalServicePrice: moneyField(),
    compensationCurrency: doc(
        z.string().nullish(),
        'Compensation currency (ISO 4217, exactly 3 letters, for example CZK). Null if not set.',
    ),
} as const;

export const claimV21ResponseSchema = z.object({
    eshopClaimId: doc(z.string(), 'Your claim identifier.'),
    eshopOrderId: doc(z.string(), 'Related order identifier.'),
    ...claimV21WritableFields,
    ...claimV21ResponseReadOnly,
}).strict();

export type CreateClaimV21 = z.infer<typeof createClaimV21Schema>;
export type UpdateClaimV21 = z.infer<typeof updateClaimV21Schema>;
export type PatchClaimV21 = z.infer<typeof patchClaimV21Schema>;
export type ClaimV21 = z.infer<typeof claimV21ResponseSchema>;
